// Split out of journey.ts (ADR-002 phase C): cycle (step) persistence
// and AI actions plus the stage-6 / offline-activity render blocks.

import { showToast } from '../shared/ui';
import { apiFetch } from '../api/client';
import { trajState, trajActions, findObservationById, escapeHtml, escapeAttr } from './journey-state';
import type { JourneyAnalysis } from './journey.types';

const STAGE6_DISTORTION_OPTIONS = [
  { id: 'особенность', label: 'Особенность / «миссия»' },
  { id: 'миссионерство', label: 'Миссионерство' },
  { id: 'псевдообщность', label: 'Псевдообщность' },
  { id: 'присвоение_статуса', label: 'Присвоение статуса ЛБ / уровня' },
] as const;

export function renderStage6Orientir(section: HTMLElement) {
  const orientir = document.createElement('div');
  orientir.className = 'stage6-orientir';
  orientir.setAttribute('role', 'note');
  orientir.innerHTML = `
    <p class="stage6-orientir-title">Ориентир: Этап 6 ЭР — Общность в действии</p>
    <p class="stage6-orientir-text">
      Это не уровень, достижение или статус в Общности. Здесь фиксируется реальное участие в поле
      без эго-фильтров — свободная запись и опциональные поля ниже, без «закрытия этапа».
    </p>
    <p class="stage6-orientir-label">На что обратить внимание при самонаблюдении:</p>
    <ul class="stage6-orientir-list">
      ${STAGE6_DISTORTION_OPTIONS.map((o) => `<li>${escapeHtml(o.label)}</li>`).join('')}
    </ul>
    <p class="stage6-orientir-note">Координация офлайн-взаимодействия в Общности — вне этого интерфейса.</p>
  `;
  section.appendChild(orientir);
}

export function renderStage6MetaPanel(stepBox: HTMLElement, cycle: any, canEdit: boolean) {
  const content = cycle?.content || {};
  const panel = document.createElement('details');
  panel.className = 'stage6-meta-panel';
  panel.open = Boolean(
    content.community_action || content.movement || content.gsn_reflection || content.distortion_checks?.length
  );
  panel.innerHTML = `
    <summary class="stage6-meta-summary">Структурированная фиксация (опционально)</summary>
    <div class="stage6-meta-grid">
      <fieldset class="stage6-meta-fieldset" ${!canEdit ? 'disabled' : ''}>
        <legend>Участие в поле</legend>
        <label>Форма участия<input type="text" class="stage6-input" data-field="community_action.participation_form" value="${escapeAttr(content.community_action?.participation_form || '')}" placeholder="Совместная практика, поддержка поля…" ${!canEdit ? 'disabled' : ''}></label>
        <label>Контекст поля<input type="text" class="stage6-input" data-field="community_action.field_context" value="${escapeAttr(content.community_action?.field_context || '')}" placeholder="ЛПО, встреча, среда…" ${!canEdit ? 'disabled' : ''}></label>
        <label>Связь с ЛПО / ЛБПО<input type="text" class="stage6-input" data-field="community_action.lpo_connection" value="${escapeAttr(content.community_action?.lpo_connection || '')}" ${!canEdit ? 'disabled' : ''}></label>
        <label>Личное vs общая ответственность<input type="text" class="stage6-input" data-field="community_action.personal_vs_shared" value="${escapeAttr(content.community_action?.personal_vs_shared || '')}" ${!canEdit ? 'disabled' : ''}></label>
      </fieldset>
      <fieldset class="stage6-meta-fieldset" ${!canEdit ? 'disabled' : ''}>
        <legend>Шаг движения</legend>
        <label>Описание<input type="text" class="stage6-input" data-field="movement.description" value="${escapeAttr(content.movement?.description || '')}" ${!canEdit ? 'disabled' : ''}></label>
        <label>Причина<input type="text" class="stage6-input" data-field="movement.reason" value="${escapeAttr(content.movement?.reason || '')}" ${!canEdit ? 'disabled' : ''}></label>
        <label>Последствия<input type="text" class="stage6-input" data-field="movement.consequences" value="${escapeAttr(content.movement?.consequences || '')}" ${!canEdit ? 'disabled' : ''}></label>
      </fieldset>
      <fieldset class="stage6-meta-fieldset" ${!canEdit ? 'disabled' : ''}>
        <legend>Глубинное звучание (ГСН)</legend>
        <label>Ситуация<input type="text" class="stage6-input" data-field="gsn_reflection.situation" value="${escapeAttr(content.gsn_reflection?.situation || '')}" ${!canEdit ? 'disabled' : ''}></label>
        <label>Решение о глубине
          <select class="stage6-input" data-field="gsn_reflection.depth_decision" ${!canEdit ? 'disabled' : ''}>
            <option value="">—</option>
            <option value="yes" ${content.gsn_reflection?.depth_decision === 'yes' ? 'selected' : ''}>Да</option>
            <option value="no" ${content.gsn_reflection?.depth_decision === 'no' ? 'selected' : ''}>Нет</option>
            <option value="undefined" ${content.gsn_reflection?.depth_decision === 'undefined' ? 'selected' : ''}>Не определено</option>
          </select>
        </label>
        <label>Риск воображения<input type="text" class="stage6-input" data-field="gsn_reflection.imagination_risk" value="${escapeAttr(content.gsn_reflection?.imagination_risk || '')}" ${!canEdit ? 'disabled' : ''}></label>
      </fieldset>
      <fieldset class="stage6-meta-fieldset stage6-distortion-fieldset" ${!canEdit ? 'disabled' : ''}>
        <legend>Маркеры искажения</legend>
        <div class="stage6-distortion-checks">
          ${STAGE6_DISTORTION_OPTIONS.map((o) => `
            <label class="stage6-distortion-check">
              <input type="checkbox" data-distortion="${o.id}" ${content.distortion_checks?.includes(o.id) ? 'checked' : ''} ${!canEdit ? 'disabled' : ''}>
              ${escapeHtml(o.label)}
            </label>
          `).join('')}
        </div>
      </fieldset>
    </div>
  `;
  stepBox.appendChild(panel);
}

function collectStage6Content(stepBox: Element, text: string): Record<string, unknown> {
  const content: Record<string, unknown> = { text };
  const blocks: Record<string, Record<string, string>> = {
    community_action: {},
    movement: {},
    gsn_reflection: {},
  };

  stepBox.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.stage6-input').forEach((el) => {
    const field = el.dataset.field;
    if (!field) return;
    const [block, key] = field.split('.');
    const value = el.value.trim();
    if (!value || !blocks[block]) return;
    blocks[block][key] = value;
  });

  for (const [block, values] of Object.entries(blocks)) {
    if (Object.keys(values).length > 0) {
      content[block] = values;
    }
  }

  const distortion_checks = Array.from(
    stepBox.querySelectorAll<HTMLInputElement>('.stage6-distortion-checks input[type="checkbox"]:checked')
  ).map((el) => el.dataset.distortion).filter(Boolean);
  if (distortion_checks.length > 0) {
    content.distortion_checks = distortion_checks;
  }

  return content;
}

export function buildCycleContent(
  scale: 'daily' | 'qualitative',
  stageIndex: number,
  text: string,
  stepBox?: Element | null
): Record<string, unknown> {
  if (scale === 'qualitative' && stageIndex === 6 && stepBox) {
    return collectStage6Content(stepBox, text);
  }
  return { text };
}

export function isCycleContentDirty(
  scale: 'daily' | 'qualitative',
  stageIndex: number,
  text: string,
  stepBox: Element,
  originalText: string
): boolean {
  if (text.trim() !== originalText.trim()) return true;
  if (scale !== 'qualitative' || stageIndex !== 6) return false;
  const current = JSON.stringify(buildCycleContent(scale, stageIndex, text, stepBox));
  const baseline =
    (stepBox instanceof HTMLElement ? stepBox.dataset.contentBaseline : undefined)
    || JSON.stringify({ text: originalText });
  return current !== baseline;
}

const OFFLINE_ACTIVITY_TYPE_OPTIONS = [
  { value: 'field_participation', label: 'Участие в поле' },
  { value: 'community_gathering', label: 'Встреча / практика Общности' },
  { value: 'movement_verification', label: 'Проверка шага движения' },
  { value: 'other', label: 'Другое' },
] as const;

function formatOfflineOccurredAt(ms: number): string {
  try {
    return new Date(ms).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(ms);
  }
}

export async function renderOfflineActivitiesSection(
  observationId: string,
  section: HTMLElement,
  canEdit: boolean,
  stage6CycleId?: string
) {
  const host = document.createElement('div');
  host.className = 'stage6-offline-section';
  host.innerHTML = `
    <h4 class="stage6-offline-title">Офлайн-фиксации</h4>
    <p class="stage6-offline-hint">Запись реального участия вне интерфейса. Это не статус и не «закрытие этапа».</p>
    <div class="stage6-offline-list" data-role="offline-list">Загрузка…</div>
    ${canEdit ? `
      <form class="stage6-offline-form" data-role="offline-form">
        <label>Тип
          <select name="activity_type" required>
            ${OFFLINE_ACTIVITY_TYPE_OPTIONS.map((o) => `<option value="${o.value}">${escapeHtml(o.label)}</option>`).join('')}
          </select>
        </label>
        <label>Краткое описание
          <input type="text" name="summary" required maxlength="500" placeholder="Что произошло в поле Общности?">
        </label>
        <label>Контекст поля
          <input type="text" name="field_context" maxlength="300" placeholder="ЛПО, встреча, среда…">
        </label>
        <label>Когда (локальное время)
          <input type="datetime-local" name="occurred_at_local">
        </label>
        <button type="submit" class="btn-secondary-sm">+ Добавить офлайн-фиксацию</button>
      </form>
    ` : ''}
  `;
  section.appendChild(host);

  const listEl = host.querySelector<HTMLElement>('[data-role="offline-list"]')!;

  const refresh = async () => {
    try {
      const res = await apiFetch<{ items: any[] }>(`/api/journey-observations/${observationId}/offline-activities`);
      const items = res?.items || [];
      if (items.length === 0) {
        listEl.innerHTML = '<p class="stage6-offline-empty">Пока нет офлайн-фиксаций для этого наблюдения.</p>';
        return;
      }
      listEl.innerHTML = items.map((item) => {
        const typeLabel = OFFLINE_ACTIVITY_TYPE_OPTIONS.find((o) => o.value === item.activity_type)?.label || item.activity_type;
        const canDelete = canEdit;
        return `
          <div class="stage6-offline-item" data-activity-id="${escapeHtml(item.id)}">
            <div class="stage6-offline-item-head">
              <strong>${escapeHtml(typeLabel)}</strong>
              <span class="stage6-offline-date">${escapeHtml(formatOfflineOccurredAt(item.occurred_at))}</span>
            </div>
            <div>${escapeHtml(item.summary)}</div>
            ${item.field_context ? `<div class="stage6-offline-context">${escapeHtml(item.field_context)}</div>` : ''}
            ${canDelete ? `<button type="button" class="btn-link stage6-offline-delete">Удалить</button>` : ''}
          </div>
        `;
      }).join('');

      listEl.querySelectorAll<HTMLButtonElement>('.stage6-offline-delete').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const itemEl = btn.closest<HTMLElement>('.stage6-offline-item');
          const activityId = itemEl?.dataset.activityId;
          if (!activityId) return;
          btn.disabled = true;
          try {
            await apiFetch(`/api/journey-offline-activities/${activityId}`, { method: 'DELETE' });
            await refresh();
            showToast('Офлайн-фиксация удалена');
          } catch (err: any) {
            showToast(err.message || 'Ошибка удаления', 'error');
            btn.disabled = false;
          }
        });
      });
    } catch (err) {
      console.error('Failed to load offline activities', err);
      listEl.innerHTML = '<p class="stage6-offline-empty">Не удалось загрузить офлайн-фиксации.</p>';
    }
  };

  const form = host.querySelector<HTMLFormElement>('[data-role="offline-form"]');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    const fd = new FormData(form);
    const localValue = String(fd.get('occurred_at_local') || '').trim();
    let occurred_at: number | undefined;
    if (localValue) {
      const parsed = new Date(localValue).getTime();
      if (Number.isFinite(parsed)) occurred_at = parsed;
    }
    try {
      await apiFetch(`/api/journey-observations/${observationId}/offline-activities`, {
        method: 'POST',
        body: JSON.stringify({
          activity_type: fd.get('activity_type'),
          summary: fd.get('summary'),
          field_context: fd.get('field_context'),
          occurred_at,
          cycle_id: stage6CycleId || null,
        }),
      });
      form.reset();
      await refresh();
      showToast('Офлайн-фиксация добавлена');
    } catch (err: any) {
      showToast(err.message || 'Ошибка сохранения', 'error');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  await refresh();
}

export async function saveCycle(
  observationId: string,
  stageIndex: number,
  cycleId: string | undefined,
  text: string,
  isAutosave = false,
  scale: 'daily' | 'qualitative' = 'daily',
  stepBox?: Element | null
) {
  if (!trajState.currentJourney) return;
  try {
    const content = buildCycleContent(scale, stageIndex, text, stepBox);
    let savedCycle: any = null;
    if (!cycleId) {
      savedCycle = await apiFetch<any>(`/api/journey-observations/${observationId}/cycles`, {
        method: 'POST',
        body: JSON.stringify({ stage_index: stageIndex, scale, content })
      });
    } else {
      savedCycle = await apiFetch<any>(`/api/journey-observation-cycles/${cycleId}`, {
        method: 'PATCH',
        body: JSON.stringify({ content })
      });
    }

    // Update local cache directly to prevent desync during typing or closing
    const match = findObservationById(observationId);
    if (match) {
      const { obs } = match;
      if (!obs.cycles) obs.cycles = [];
      if (!cycleId) {
        obs.cycles.push(savedCycle);
      } else {
        const localCycle = obs.cycles.find(s => s.id === cycleId);
        if (localCycle) {
          localCycle.content = savedCycle?.content || content;
          if (savedCycle?.content_schema_version) {
            localCycle.content_schema_version = savedCycle.content_schema_version;
          }
        }
      }
    }

    if (stepBox instanceof HTMLElement && scale === 'qualitative' && stageIndex === 6) {
      stepBox.dataset.contentBaseline = JSON.stringify(content);
    }

    if (!isAutosave) {
      showToast('Наблюдение успешно сохранено');
      await trajActions.reloadDetails(trajState.currentJourney.id);
    }
  } catch (err: any) {
    showToast(err.message || 'Ошибка сохранения наблюдения', 'error');
  }
}

export async function analyzeCycle(cycleId: string, container: HTMLElement, btn: HTMLButtonElement) {
  if (!trajState.currentJourney) return;
  const originalText = btn.textContent;
  btn.textContent = 'Анализируем...';
  btn.disabled = true;
  container.innerHTML = '<div style="font-size: 0.8rem; color: var(--muted); margin-top: 8px;">Запуск анализа с проверкой Базы знаний...</div>';

  try {
    await apiFetch<JourneyAnalysis>(`/api/journey-observation-cycles/${cycleId}/analyze`, {
      method: 'POST'
    });
    showToast('Анализ завершен');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    console.error('Analysis failed', err);
    if (err.status === 402) {
      container.innerHTML = `
        <div style="margin-top: 8px; padding: var(--sp-sm); background: rgba(239, 68, 68, 0.1); border: 1px solid var(--danger); border-radius: 4px; font-size: 0.8rem; color: #f87171;">
          ⚠️ <strong>Недостаточно средств (Billing Guard)</strong><br>
          Для запуска анализа требуется пополнить баланс аккаунта.
        </div>
      `;
      showToast('Недостаточно средств на балансе', 'error');
    } else {
      container.innerHTML = `<div style="font-size: 0.8rem; color: var(--danger); margin-top: 8px;">Ошибка: ${escapeHtml(err.message || 'Неизвестная ошибка')}</div>`;
      showToast(err.message || 'Ошибка при запуске анализа', 'error');
    }
  } finally {
    btn.textContent = originalText;
    btn.disabled = false;
  }
}

export async function assistCycle(
  observationId: string,
  stageIndex: number,
  textarea: HTMLTextAreaElement,
  container: HTMLElement,
  btn: HTMLButtonElement
) {
  if (!trajState.currentJourney) return;
  const originalText = btn.textContent;
  btn.textContent = '🪄 Думает...';
  btn.disabled = true;
  container.innerHTML = '<div class="assistant-loading">🪄 Запрос к ИИ-ассистенту...</div>';

  try {
    const res = await apiFetch<{ suggestion: string; cost_usd: number }>(
      `/api/journey-observations/${observationId}/cycles/assist`,
      {
        method: 'POST',
        body: JSON.stringify({ stage_index: stageIndex, current_text: textarea.value })
      }
    );

    const suggestion = res?.suggestion || '';
    if (!suggestion.trim()) {
      container.innerHTML = '<div class="assistant-error">ИИ не смог предложить вариант. Попробуйте ввести больше контекста.</div>';
      return;
    }

    container.innerHTML = `
      <div class="assistant-suggestion-box">
        <div class="assistant-suggestion-header">
          <span>🪄 Предложение ИИ-ассистента</span>
          <span class="assistant-cost">($${res.cost_usd.toFixed(4)})</span>
        </div>
        <div class="assistant-suggestion-body">${escapeHtml(suggestion).replace(/\n/g, '<br>')}</div>
        <div class="assistant-suggestion-actions">
          <button type="button" class="btn-primary-sm accept-replace-btn">Принять и заменить</button>
          <button type="button" class="btn-secondary-sm accept-append-btn">Дописать в конец</button>
          <button type="button" class="btn-secondary-sm hide-assistant-btn">Скрыть</button>
        </div>
      </div>
    `;

    // Привязываем события кнопкам
    const replaceBtn = container.querySelector('.accept-replace-btn');
    const appendBtn = container.querySelector('.accept-append-btn');
    const hideBtn = container.querySelector('.hide-assistant-btn');

    replaceBtn?.addEventListener('click', () => {
      textarea.value = suggestion;
      textarea.dispatchEvent(new Event('input')); // чтобы активировать кнопку "Сохранить"
      container.innerHTML = '';
      showToast('Текст заменен. Не забудьте сохранить изменения.');
    });

    appendBtn?.addEventListener('click', () => {
      const curVal = textarea.value.trim();
      textarea.value = curVal ? `${curVal}\n\n${suggestion}` : suggestion;
      textarea.dispatchEvent(new Event('input'));
      container.innerHTML = '';
      showToast('Текст добавлен. Не забудьте сохранить изменения.');
    });

    hideBtn?.addEventListener('click', () => {
      container.innerHTML = '';
    });

  } catch (err: any) {
    console.error('AI assistance failed', err);
    if (err.status === 402) {
      container.innerHTML = `
        <div class="assistant-billing-error">
          ⚠️ <strong>Недостаточно средств (Billing Guard)</strong><br>
          Для получения подсказки ИИ требуется пополнить баланс аккаунта.
        </div>
      `;
      showToast('Недостаточно средств на балансе', 'error');
    } else {
      container.innerHTML = `<div class="assistant-error">Ошибка: ${escapeHtml(err.message || 'Неизвестная ошибка')}</div>`;
      showToast(err.message || 'Ошибка при получении подсказки', 'error');
    }
  } finally {
    btn.textContent = originalText;
    btn.disabled = false;
  }
}

function renderAnalysisResult(analysis: JourneyAnalysis, container: HTMLElement) {
  const res = analysis.result;
  if (!res) return;

  const hypotheses = res.distortion_hypotheses || [];
  const context = res.journey_context;
  const questions = res.questions || [];
  const warnings = res.warnings || [];
  const cost = analysis.cost_usd ? analysis.cost_usd.toFixed(5) : '0.00000';

  let warningsHtml = '';
  if (warnings.length > 0) {
    const warningLabels: Record<string, string> = {
      possible_self_justification: 'Самооправдание (попытка защитить эго-образ)',
      possible_spiritual_ego: 'Духовное эго (самообман через терминологию)',
      possible_premature_action: 'Преждевременное действие (попытка сбежать от переживания)'
    };
    warningsHtml = `
      <div class="analysis-block analysis-block-warnings">
        <div class="analysis-block-title">⚠️ Обнаружены риски защиты эго:</div>
        <ul class="analysis-list">
          ${warnings.map(w => `<li>${escapeHtml(warningLabels[w] || w)}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  let questionsHtml = '';
  if (questions.length > 0) {
    questionsHtml = `
      <div class="analysis-block analysis-block-questions">
        <div class="analysis-block-title">❓ Вопросы для созерцания:</div>
        <ul class="analysis-list">
          ${questions.map(q => `<li>${escapeHtml(q)}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  let hypothesesHtml = '';
  if (hypotheses.length > 0) {
    hypothesesHtml = `
      <div class="rag-refs-title" style="margin-top: 10px; margin-bottom: 6px;">Выявленные эго-искажения:</div>
      <div class="hypotheses-list">
        ${hypotheses.map(h => {
          const confidenceLabels: Record<string, string> = { high: 'Высокая', medium: 'Средняя', low: 'Низкая' };
          return `
            <div class="hypothesis-card confidence-${h.confidence}">
              <div class="hypothesis-title-row">
                <span>${escapeHtml(h.name)}</span>
                <span class="confidence-badge">${confidenceLabels[h.confidence] || h.confidence}</span>
              </div>
              <div class="hypothesis-mechanism">${escapeHtml(h.mechanism)}</div>
              ${h.rag_refs && h.rag_refs.length > 0 ? `
                <div class="rag-refs-title">Ссылки на контекст реальности (База знаний):</div>
                ${h.rag_refs.map(ref => `
                  <div class="rag-ref-item">
                    <div class="rag-ref-quote">«${escapeHtml(ref.quote)}»</div>
                    <span class="rag-ref-source">${escapeHtml(ref.source)} • Релевантность: ${escapeHtml(ref.relevance)}</span>
                  </div>
                `).join('')}
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  let contextHtml = '';
  if (context) {
    const sphereNames: Record<string, string> = { work: 'Работа', relation: 'Отношения', project: 'Проекты' };
    const statusLabels: Record<string, string> = {
      alive: 'Живой',
      dead: 'Мертвый',
      ambiguous: 'Двоякий',
      red_zone: 'Красная зона',
      unknown: 'Неизвестно'
    };
    contextHtml = `
      <div class="journey-context-block">
        <div class="context-row">
          <span class="context-label">Сфера:</span>
          <span class="context-val">${sphereNames[context.element_category] || context.element_category}</span>
        </div>
        <div class="context-row">
          <span class="context-label">Рекомендуемое состояние:</span>
          <span class="context-val status-${context.possible_state}" style="padding: 1px 6px; border-radius: 4px;">${statusLabels[context.possible_state] || context.possible_state}</span>
        </div>
        <div class="context-reason">
          <strong>Обоснование:</strong> ${escapeHtml(context.reason)}
        </div>
      </div>
    `;
  }

  container.innerHTML = `
    <div class="step-analysis-container">
      <div class="analysis-header">
        <span>🔍 Экспертный ЭР-анализ</span>
        <span style="font-size: 0.7rem; color: var(--muted); font-weight: normal; margin-left: auto; display: inline-flex; align-items: center; gap: 4px;">Использовано: <i class="fa-solid fa-gem" style="color: #34d399; font-size: 0.7rem;"></i> ${cost}</span>
      </div>
      ${contextHtml}
      ${hypothesesHtml}
      <div class="analysis-meta-blocks" style="margin-top: 10px;">
        ${warningsHtml}
        ${questionsHtml}
      </div>
    </div>
  `;
}
